import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Flame, TrendingUp, Sparkles, ArrowRight, BookOpen, ShieldCheck, Clock, BadgeCheck, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DealCard from "../components/DealCard";
import AdSlot from "../components/AdSlot";
import DealsMarquee from "../components/DealsMarquee";
import HeroCarousel from "../components/HeroCarousel";
import BlogCarousel from "../components/BlogCarousel";
import Seo, { JsonLd, schemas } from "../components/Seo";
import PushBanner from "../components/PushBanner";
import { api, STORE_LABEL, priceFmt, dealTitle } from "../lib/api";

const STORES = ["all", "amazon", "aliexpress", "ebay"];
const SORTS = [
  { k: "recent", label: "Più recenti" },
  { k: "discount", label: "Sconto maggiore" },
  { k: "popular", label: "Più cliccate" },
];

// Hydrate the initial render from the prerender bootstrap payload so the
// hero counters and first cards appear immediately (no skeleton flash).
const __INITIAL__ = (typeof window !== "undefined" && window.__BDD_INITIAL__) || null;
const initialDeals = __INITIAL__?.deals || null;
const initialStats = __INITIAL__?.stats
  ? { total_deals: __INITIAL__.stats.total, hot_deals: __INITIAL__.stats.hot, total_clicks: 0 }
  : null;
const initialArticles = __INITIAL__?.articles || null;

export default function Home() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const store = params.get("store") || "all";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "recent";
  const hotFilter = params.get("hot") === "1" || params.get("hot") === "true";

  const { data: statsData } = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get("/stats").then((r) => r.data),
    initialData: initialStats || undefined,
  });
  const stats = statsData || { total_deals: 0, hot_deals: 0, total_clicks: 0 };

  // Use prerender data as initial paint when no filters are active
  const filtersActive = category !== "all" || store !== "all" || search || sort !== "recent" || hotFilter;
  const dealsInitial = !filtersActive && initialDeals ? initialDeals.slice(0, 24) : undefined;

  const { data: dealsData, isLoading } = useQuery({
    queryKey: ["deals", category, store, search, sort, hotFilter],
    queryFn: () => {
      const reqParams = { category, store, search, sort, limit: 24 };
      if (hotFilter) reqParams.hot = true;
      return api.get("/deals", { params: reqParams }).then((r) => r.data.items);
    },
    initialData: dealsInitial,
  });
  const deals = dealsData || [];
  const loading = isLoading && !dealsInitial;

  const hotInitial = initialDeals ? initialDeals.filter((d) => d.hot).slice(0, 6) : undefined;
  const { data: hotData } = useQuery({
    queryKey: ["hot"],
    queryFn: () =>
      api
        .get("/deals", { params: { hot: true, limit: 6, sort: "discount" } })
        .then((r) => r.data.items),
    initialData: hotInitial,
  });
  const hot = hotData || [];

  const { data: articlesData } = useQuery({
    queryKey: ["home-articles"],
    queryFn: () => api.get("/articles", { params: { limit: 6 } }).then((r) => r.data.items),
    staleTime: 5 * 60_000,
    initialData: initialArticles ? initialArticles.slice(0, 6) : undefined,
  });
  const articles = articlesData || [];

  const { data: tgData } = useQuery({
    queryKey: ["tg-subs"],
    queryFn: () => api.get("/telegram-subscribers").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const tgSubs = tgData?.count || 0;

  // Hero: trending deals (last 7 days clicks) — powers the "in fiamme" ticker
  const { data: trendingData } = useQuery({
    queryKey: ["hero-trending"],
    queryFn: () => api.get("/trending-deals", { params: { limit: 3 } }).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const trending = trendingData?.items || [];

  // Freshness chip: minutes since latest published deal
  const latestDealTs = deals[0]?.created_at || initialDeals?.[0]?.created_at;
  const minutesSinceLatest = latestDealTs
    ? Math.max(1, Math.round((Date.now() - new Date(latestDealTs).getTime()) / 60000))
    : null;

  // Hero carousel uses up to 5 hot deals (fallback to recent if not enough hot)
  const heroDeals = hot.length >= 2 ? hot.slice(0, 5) : deals.slice(0, 5);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "all") p.delete(k);
    else p.set(k, v);
    setParams(p);
  };

  return (
    <div className="min-h-screen bg-[var(--bdd-bg)]">
      <Seo
        title="Offerte set di costruzioni in tempo reale"
        description="Le migliori offerte ogni giorno su set di costruzioni: Amazon, eBay, AliExpress. Sconti curati, alert in tempo reale, canale Telegram per non perdere nessuna offerta lampo."
        path="/"
      />
      <JsonLd data={schemas.itemList(deals)} />
      <Header />
      <PushBanner />
      <DealsMarquee />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900" data-testid="hero-section">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="absolute inset-0 brick-pattern opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              {/* Freshness chip — replaces static "Nuove offerte ogni giorno" */}
              <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full pl-2 pr-3 py-1 mb-6">
                {minutesSinceLatest && minutesSinceLatest < 60 * 24 ? (
                  <>
                    <span className="relative flex h-2 w-2 ml-1">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-70"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                    </span>
                    <span className="text-xs font-mono-bdd uppercase tracking-[0.25em] text-zinc-300" data-testid="hero-freshness-chip">
                      Nuova offerta{" "}
                      {minutesSinceLatest < 60
                        ? `${minutesSinceLatest} min fa`
                        : `${Math.round(minutesSinceLatest / 60)}h fa`}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles size={12} className="text-[#FBD51C]" />
                    <span className="text-xs font-mono-bdd uppercase tracking-[0.25em] text-zinc-400">
                      Nuove offerte ogni giorno
                    </span>
                  </>
                )}
              </div>

              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl text-white leading-[0.92] tracking-tighter">
                Le offerte migliori
                <br />
                sui <span className="text-gold-grad">set di costruzioni</span>.
              </h1>
              <p className="text-zinc-400 text-base sm:text-lg mt-6 max-w-xl">
                Selezione manuale di sconti, lampo e crollo prezzo dai principali
                store. Amazon, AliExpress, eBay — un solo posto, zero rumore.
              </p>

              {/* Trust badges (Option A) — replaces the useless CTA buttons + stat boxes */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl" data-testid="hero-trust-badges">
                <TrustBadge icon={<BadgeCheck size={16} />} title="Selezione manuale" subtitle="No dropshipping" />
                <TrustBadge icon={<Clock size={16} />} title="Prezzi verificati" subtitle="Aggiornati continuamente" />
                <TrustBadge icon={<ShieldCheck size={16} />} title="Solo sconti reali" subtitle="No prezzi gonfiati" />
              </div>

              {/* Trending ticker (Option C) — 3 thumbnails of the "in fiamme" deals */}
              {trending.length > 0 && (
                <div
                  className="mt-6 rounded-2xl bg-gradient-to-br from-zinc-950 to-zinc-900/60 border border-zinc-800 p-4"
                  data-testid="hero-trending-ticker"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 bg-orange-500/15 text-orange-400 border border-orange-500/30 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                        <Flame size={11} /> Hot
                      </span>
                      <span className="text-xs text-zinc-500 hidden sm:inline">
                        Le più cliccate degli ultimi 7 giorni
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {trending.map((d, i) => (
                      <Link
                        key={d.id}
                        to={`/offerta/${d.id}`}
                        data-testid={`hero-trending-item-${i}`}
                        className="group relative rounded-xl overflow-hidden bg-zinc-950 border border-zinc-800 hover:border-orange-500/50 transition-colors"
                      >
                        <div className="aspect-square bg-zinc-900 overflow-hidden">
                          {d.image_url ? (
                            <img
                              src={d.image_url}
                              alt={dealTitle(d)}
                              loading="lazy"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : null}
                        </div>
                        {d.discount_percent ? (
                          <div className="absolute top-1.5 right-1.5 bg-rose-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded">
                            -{Math.round(d.discount_percent)}%
                          </div>
                        ) : null}
                        <div className="p-2">
                          <div className="text-[10px] text-zinc-500 font-mono-bdd uppercase tracking-wider truncate">
                            {STORE_LABEL[d.store] || d.store}
                          </div>
                          <div className="text-xs text-white font-bold line-clamp-2 leading-tight mb-1 min-h-[32px]">
                            {dealTitle(d)}
                          </div>
                          {d.price_current ? (
                            <div className="text-sm font-black text-[#FBD51C]">
                              {priceFmt(d.price_current)}
                            </div>
                          ) : null}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* CTAs — telegram social-proof only */}
              <div className="flex flex-wrap gap-3 mt-6 items-center">
                {tgSubs > 0 && (
                  <a
                    href="https://t.me/BrickDailyDeals"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-tg-social-proof"
                    className="inline-flex items-center gap-2 bg-[#229ED9]/15 hover:bg-[#229ED9]/25 text-[#86d2f5] border border-[#229ED9]/30 font-bold text-sm px-4 py-3 rounded-lg transition-colors"
                  >
                    <Send size={14} />
                    {tgSubs.toLocaleString("it-IT")} su Telegram
                  </a>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <HeroCarousel deals={heroDeals} />
            </div>
          </div>
        </div>
      </section>

      {/* Ultimi dal blog — featured section (P1: showcase editorial content for AdSense) */}
      {articles.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14"
          data-testid="home-blog-section"
        >
          <div className="flex items-end justify-between mb-6 gap-4">
            <SectionTitle
              kicker="Editoriale"
              title="Guide e approfondimenti"
              subtitle="Consigli, recensioni e analisi per scegliere il set giusto"
              icon={<BookOpen size={18} className="text-[#FBD51C]" />}
            />
            <Link
              to="/guide"
              data-testid="home-all-articles"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono-bdd uppercase tracking-wider text-[#FBD51C] hover:text-white transition-colors"
            >
              Tutti gli articoli <ArrowRight size={12} />
            </Link>
          </div>
          <BlogCarousel articles={articles} max={6} />
          <div className="mt-6 sm:hidden text-center">
            <Link
              to="/guide"
              className="inline-flex items-center gap-1.5 text-xs font-mono-bdd uppercase tracking-wider text-[#FBD51C]"
            >
              Tutti gli articoli <ArrowRight size={12} />
            </Link>
          </div>
        </section>
      )}

      {/* AD slot under hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <AdSlot id="below-hero" size="leaderboard" />
      </div>

      {/* HOT carousel strip */}
      {hot.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="hot-section">
          <SectionTitle
            kicker="Selezione di oggi"
            title="Offerte HOT"
            subtitle="I crolli di prezzo più interessanti del momento"
            icon={<Flame size={18} className="text-rose-500" />}
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {hot.slice(0, 6).map((d, i) => (
              <DealCard key={d.id} deal={d} idx={i} />
            ))}
          </div>
        </section>
      )}

      {/* MAIN listing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10" data-testid="deals-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <SectionTitle
            kicker={search ? `Risultati per "${search}"` : "Catalogo completo"}
            title="Tutte le offerte"
            subtitle="Filtra per store, categoria o ordina come preferisci"
          />
          <div className="flex flex-wrap gap-2 items-center">
            {STORES.map((s) => (
              <button
                key={s}
                data-testid={`filter-store-${s}`}
                onClick={() => setParam("store", s)}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all ${
                  store === s
                    ? "bg-[#FBD51C] text-black"
                    : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700"
                }`}
              >
                {s === "all" ? "Tutti" : STORE_LABEL[s]}
              </button>
            ))}
            <div className="h-5 w-px bg-zinc-800 mx-1" />
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full outline-none focus:border-[#FBD51C]/60"
            >
              {SORTS.map((s) => (
                <option key={s.k} value={s.k}>
                  {s.label}
                </option>
              ))}
            </select>
            <button
              data-testid="hot-filter-toggle"
              onClick={() => setParam("hot", hotFilter ? null : "1")}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full transition-all inline-flex items-center gap-1.5 ${
                hotFilter
                  ? "bg-rose-500/20 text-rose-300 border border-rose-500/40"
                  : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700"
              }`}
            >
              <Flame size={11} /> Solo HOT
            </button>
            {(hotFilter || sort !== "recent" || category !== "all" || store !== "all" || search) && (
              <button
                data-testid="reset-filters"
                onClick={() => setParams({})}
                className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-full bg-zinc-900 text-zinc-400 border border-zinc-800 hover:border-zinc-700"
              >
                ✕ Reset
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl shimmer" />
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div
            data-testid="no-deals"
            className="text-center py-20 border border-dashed border-zinc-800 rounded-2xl"
          >
            <div className="font-display font-black text-2xl text-white mb-2">
              Nessuna offerta trovata
            </div>
            <p className="text-zinc-500 text-sm">
              Prova a cambiare filtri o torna più tardi: aggiorniamo ogni giorno.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {deals.map((d, i) => (
              <React.Fragment key={d.id}>
                <DealCard deal={d} idx={i} />
                {i === 7 && (
                  <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                    <AdSlot id="mid-grid" size="banner" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </section>

      {/* Bottom ad */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <AdSlot id="bottom" size="leaderboard" />
      </div>

      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
      <div className="font-display font-black text-2xl text-white leading-none">
        {value}
      </div>
      <div className="font-mono-bdd text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function TrustBadge({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-2.5 bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 hover:border-[#FBD51C]/30 transition-colors">
      <div className="mt-0.5 h-8 w-8 flex items-center justify-center rounded-lg bg-[#FBD51C]/10 text-[#FBD51C] shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold text-white leading-tight">
          {title}
        </div>
        <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">
          {subtitle}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ kicker, title, subtitle, icon }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <div className="font-mono-bdd text-[11px] uppercase tracking-[0.3em] text-[#FBD51C]">
          {kicker}
        </div>
      </div>
      <h2 className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
        {title}
      </h2>
      {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  );
}
